import {
  FieldValue,
  FieldValues,
  getComponentSubtree,
  stripComponents,
} from 'features/libra/ontology/ontologyFieldTypes'
import { useComponentScope } from 'features/libra/workspace/ComponentScopeContext'
import { useCallback } from 'react'
import { RolodexEntryTagsType } from 'types'

import { applyOriginOption, FieldOriginOption } from './fieldOrigin'
import { buildFieldValuesPatch } from './fieldValuesPatch'
import { useTicketAccess } from './TicketAccessContext'
import { useTicketWriter } from './TicketWriterContext'

/**
 * Scope discriminator for field access. The legacy `useTicketAccess()`
 * corresponds to `{ kind: 'ticket' }`; tabbed step components call
 * `useScopedFieldAccess({ kind: 'component', componentId })`.
 *
 * The discriminator stays a union (vs. a `string | null` `componentId` arg)
 * because the two callers (`useWorkflowFieldValues`,
 * `FfsMinimumThicknessStep`) need to make a SINGLE unconditional hook call
 * that internally branches; a `componentId: string | null` API would force
 * them to choose between `useScopedFieldAccess` and `useTicketAccess` at
 * render time, violating react-hooks/rules-of-hooks.
 */
export type FieldAccessScopePath =
  | { kind: 'ticket' }
  | { kind: 'component'; componentId: string }

export interface ScopedFieldAccess {
  fieldValues: FieldValues
  setFieldValue: (
    fieldId: string,
    fieldValue: FieldValue,
    option?: FieldOriginOption
  ) => Promise<void>
  setFieldValues: (
    values: Partial<FieldValues>,
    option?: FieldOriginOption
  ) => Promise<boolean>
  readOnly: boolean
  isLoading: boolean
  ticketTags: RolodexEntryTagsType
}

const emptyFieldValues: FieldValues = Object.freeze({}) as FieldValues

/**
 * Scope-aware field access.
 *
 * `kind: 'ticket'` proxies to `useTicketAccess()` for writes; on read, applies
 * `stripComponents(ticket.fieldValues)` so top-level form consumers never see
 * the reserved `components` nested map (which would otherwise round-trip into
 * their local state and clobber per-component values on save). The
 * `TicketAccessContext.setFieldValues` writer preserves the `components`
 * subtree unconditionally, so this hook doesn't need to.
 *
 * `kind: 'component'` reads `ticket.fieldValues.components[componentId]` and
 * enqueues writes that mutate only that per-component slice. Writing to a
 * previously-empty component id materializes the entry — there is no
 * pre-flight scope check because the seed step is gone (the deleted heal in
 * the componentScopes refactor). Component identity is authoritative via the
 * unit's live component list (`useLibraAssetModelDataForUnit`), and stray
 * writes to nonexistent component ids are cleaned up as orphans at read time.
 */
export const useScopedFieldAccess = (
  scope: FieldAccessScopePath
): ScopedFieldAccess => {
  const access = useTicketAccess()
  const { enqueue } = useTicketWriter()
  const {
    readOnly,
    isLoading,
    ticketTags,
    fieldValues: rawTicketFieldValues,
  } = access
  // Depend on the scope's primitive parts (not the object, which callers
  // recreate per render) so the callback identity is tied to the scope's
  // identity.
  const scopeKind = scope.kind
  const scopedComponentId =
    scope.kind === 'component' ? scope.componentId : undefined

  const setFieldValues = useCallback(
    async (
      values: Partial<FieldValues>,
      option?: FieldOriginOption
    ): Promise<boolean> => {
      if (readOnly) return false
      if (scopeKind === 'ticket' || scopedComponentId === undefined) {
        return access.setFieldValues(values as FieldValues, option)
      }
      const applied = await enqueue(
        (latest) => ({
          // Merge rules (subtree preservation, wrapper-metadata-preserving
          // merge) live in ONE place; see `buildFieldValuesPatch`.
          fieldValues: buildFieldValuesPatch(
            latest.fieldValues,
            { kind: 'component', componentId: scopedComponentId },
            applyOriginOption(values as FieldValues, option)
          ),
        }),
        { applyInteractionSideEffects: true }
      )
      return applied !== null
    },
    [readOnly, scopeKind, scopedComponentId, access, enqueue]
  )

  const setFieldValue = useCallback(
    async (
      fieldId: string,
      fieldValue: FieldValue,
      option?: FieldOriginOption
    ): Promise<void> => {
      await setFieldValues({ [fieldId]: fieldValue }, option)
    },
    [setFieldValues]
  )

  if (scope.kind === 'ticket') {
    return {
      // Strip the reserved `components` nested map from the top-level slice
      // handed to ticket-scope form consumers.
      fieldValues: stripComponents(rawTicketFieldValues),
      setFieldValue,
      setFieldValues,
      readOnly,
      isLoading,
      ticketTags,
    }
  }

  const componentFieldValues =
    getComponentSubtree(rawTicketFieldValues)?.[scope.componentId] ??
    emptyFieldValues

  return {
    fieldValues: componentFieldValues,
    setFieldValue,
    setFieldValues,
    readOnly,
    isLoading,
    ticketTags,
  }
}

/**
 * Field access for the ACTIVE scope: component-scoped when a
 * `ComponentScopeContext` is mounted (inside `UnitComponentTabs`), else
 * ticket scope. One call replaces the scope-derivation ternary consumers
 * used to re-implement at every call site.
 */
export function useActiveScopedFieldAccess(): ScopedFieldAccess {
  const componentScope = useComponentScope()
  return useScopedFieldAccess(
    componentScope
      ? { kind: 'component', componentId: componentScope.componentId }
      : { kind: 'ticket' }
  )
}
